import './Header.css';
import { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { AnimatePresence, m } from "framer-motion";
import { Menu, X } from "lucide-react";
import Wordmark from "../ui/Wordmark";
import { playStoreUrl, withUtm, trackCta } from "../../utils/tracking";

const navItems = [
    { label: "Home", to: "/" },
    { label: "Prepaid card", to: "/card" },
    { label: "Marketplace", to: "/marketplace" },
    { label: "AI Agents", to: "/ai-agents", badge: "New" },
    { label: "About us", to: "/about-us" },
    { label: "FAQs", to: "/faqs" },
    { label: "Blogs", href: withUtm("https://blog.accruewise.in", "nav", "nav") },
];

function NavItem({ item, onClick }) {
    if (item.href) {
        return (
            <a
                href={item.href}
                target="_blank"
                rel="noopener noreferrer"
                className="nav-link"
                onClick={onClick}
            >
                {item.label}
            </a>
        );
    }
    return (
        <NavLink
            to={item.to}
            end={item.to === "/"}
            onClick={onClick}
            className={({ isActive }) => `nav-link${isActive ? " nav-link--active" : ""}`}
        >
            {item.label}
            {item.badge ? <span className="nav-badge">{item.badge}</span> : null}
        </NavLink>
    );
}

export default function Header() {
    const [isOpen, setIsOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 12);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const closeMenu = () => setIsOpen(false);

    return (
        <header className={`site-header fixed top-0 left-0 right-0 z-50 h-[72px]${scrolled ? " site-header--scrolled" : ""}`}>
            <div className="relative mx-auto flex h-full w-full max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">

                <Link to="/" onClick={closeMenu} className="flex shrink-0 items-center">
                    <Wordmark />
                </Link>

                <nav className="absolute left-1/2 hidden -translate-x-1/2 items-center gap-8 lg:flex" aria-label="Primary">
                    {navItems.map((item) => (
                        <NavItem key={item.label} item={item} />
                    ))}
                </nav>

                <div className="hidden shrink-0 lg:block">
                    <a
                        href={playStoreUrl("header")}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="ed-cta"
                        onClick={() => trackCta("header")}
                    >
                        Download app
                    </a>
                </div>

                <button
                    type="button"
                    onClick={() => setIsOpen(!isOpen)}
                    className="text-ink lg:hidden"
                    aria-label={isOpen ? "Close menu" : "Open menu"}
                    aria-expanded={isOpen}
                >
                    {isOpen ? <X size={22} /> : <Menu size={22} />}
                </button>

                <AnimatePresence>
                    {isOpen ? (
                        <m.div
                            key="mobile-menu"
                            initial={{ opacity: 0, y: -8 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -8 }}
                            transition={{ duration: 0.2 }}
                            className="absolute left-4 right-4 top-[72px] z-50 lg:hidden"
                        >
                            <div className="flex flex-col gap-4 rounded-xl border border-line bg-card p-5">
                                {navItems.map((item) => (
                                    <NavItem key={item.label} item={item} onClick={closeMenu} />
                                ))}
                                <a
                                    href={playStoreUrl("header_mobile")}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="ed-cta mt-2 self-start"
                                    onClick={() => trackCta("header_mobile")}
                                >
                                    Download app
                                </a>
                            </div>
                        </m.div>
                    ) : null}
                </AnimatePresence>
            </div>
        </header>
    );
}
